import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, TrendingUp, Store } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      navigate(u?.role === "dangoods" ? "/dangoods" : u?.role === "tradepartner" ? "/tradepartner" : u?.role === "stockgroup" ? "/stockgroup" : "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* Left: form */}
      <div className="flex items-center justify-center px-6 py-16 sm:px-16">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="flex items-center gap-2 mb-14">
            <div className="w-9 h-9 bg-[#0A0A0A] flex items-center justify-center">
              <Store className="w-5 h-5 text-white" strokeWidth={1.8} />
            </div>
            <span className="font-heading font-bold tracking-tight text-lg">Customers First</span>
          </div>

          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-3">
            KPI Dashboard
          </p>
          <h1 className="font-heading text-4xl font-light tracking-tight text-slate-900 mb-2">
            Log ind
          </h1>
          <p className="text-sm text-slate-500 mb-10">
            Samlet salgsoverblik for Falster &amp; Ringsted.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-500">
                E-mail
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="din@email.dk"
                className="h-11 rounded-none border-slate-300 focus-visible:ring-slate-900"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-500">
                Adgangskode
              </Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11 rounded-none border-slate-300 focus-visible:ring-slate-900"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="login-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-none bg-[#1A1A1A] hover:bg-slate-800 text-white font-medium tracking-wide transition-colors"
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log ind"}
            </Button>
          </form>
        </div>
      </div>

      {/* Right: brand block */}
      <div className="hidden lg:flex relative bg-[#2B4C59] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]"
             style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "56px 56px" }} />
        <div className="absolute -bottom-24 -right-16 w-96 h-96 rounded-full bg-[#E05D3A]/30 blur-3xl" />
        <div className="relative z-10 flex flex-col justify-between p-16 text-white w-full">
          <div className="flex items-center gap-2 text-white/70">
            <TrendingUp className="w-5 h-5" strokeWidth={1.6} />
            <span className="text-xs uppercase tracking-[0.25em]">Sales Intelligence</span>
          </div>
          <div>
            <h2 className="font-heading text-5xl font-light leading-tight tracking-tight mb-6">
              Alt dit salg.<br />Ét sted.
            </h2>
            <p className="text-white/60 max-w-sm leading-relaxed">
              Sammenlign omsætning, ordrer og gennemsnitlig kurv på tværs af Falster og Ringsted — i realtid.
            </p>
            <div className="flex gap-8 mt-12">
              <div>
                <div className="w-3 h-3 bg-[#E05D3A] mb-2" />
                <span className="text-xs uppercase tracking-widest text-white/70">Falster</span>
              </div>
              <div>
                <div className="w-3 h-3 bg-white mb-2" />
                <span className="text-xs uppercase tracking-widest text-white/70">Ringsted</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
